import React, { useState, useEffect } from 'react';
import { apiClient } from '../../components/api/ApiClient';
import { useNavigate } from 'react-router-dom';

const formatPrice = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + ' ریال';
};

function SubscriptionPlans() {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [isDisabled, setIsDisabled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [bankModal, setBankModal] = useState({ show: false, planId: null });
    const [paymentLoading, setPaymentLoading] = useState(false);

    const subscriptionFeatures = [
        'ارسال نامحدود صورتحساب الکترونیکی',
        'پشتیبانی ۲۴ ساعته',
        'دسترسی به گزارشات پیشرفته',
        'به‌روزرسانی خودکار قوانین مالیاتی'
    ];

    useEffect(() => {
        async function fetchPlans() {
            try {
                const response = await apiClient('/subscribe/api/');
                let planData = response.data || [];
                
                const priorityOrder = [5, 6, 7, 1, 2, 3];
                planData = planData.sort((a, b) => {
                    const indexA = priorityOrder.indexOf(a.id);
                    const indexB = priorityOrder.indexOf(b.id);
                    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
                });
                
                setPlans(planData);
                setIsDisabled(response.disable || false);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        fetchPlans();
    }, []);

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => setToast({ ...toast, show: false }), 5000);
            return () => clearTimeout(timer);
        }
    }, [toast.show]);

    const addSubscribe = async (target) => {
        try {
            await apiClient('/subscribe/api/add/', { method: 'POST', data: { target } });
            
            const selectedPlan = plans.find(p => p.id === target);
            if (selectedPlan && (selectedPlan.price === 0 || selectedPlan.id === 5)) {
                setPlans(prevPlans => prevPlans.map(p => 
                    p.id === target ? { ...p, selected: true } : p
                ));
            }
            
            setToast({
                type: 'success',
                message: 'اشتراک شما با موفقیت فعال شده است',
                show: true
            });
            const timer = setTimeout(() => {
                navigate('/dashboard');
            }, 1500);
            return () => clearTimeout(timer);
        } catch (e) {
            const errorMessage = e.message || e.response?.data?.message || e.response?.data?.detail || 'مشکلی پیش آمده است!';
            setToast({
                type: 'error',
                message: errorMessage,
                show: true
            });
        }
    };

    const handleOpenBankModal = (planId) => {
        setBankModal({ show: true, planId });
    };

    const handleCloseBankModal = () => {
        if (!paymentLoading) {
            setBankModal({ show: false, planId: null });
        }
    };

    const goToBank = async () => {
        try {
            setPaymentLoading(true);
            const response = await apiClient('/subscribe/api/plan/create/', { 
                method: 'POST', 
                data: { target: bankModal.planId } 
            });
            
            if (response && response.url) {
                window.location.href = response.url;
            } else {
                setToast({
                    type: 'error',
                    message: "خطا در دریافت لینک پرداخت لطفا دوباره تلاش کنید",
                    show: true
                });
                setPaymentLoading(false);
            }
        } catch (e) {
            const errorMessage = e.message || e.response?.data?.message || e.response?.data?.error || 'مشکلی پیش آمده است!';
            setToast({
                type: 'error',
                message: errorMessage,
                show: true
            });
            setPaymentLoading(false);
            handleCloseBankModal();
        }
    };

    const renderCard = (plan) => {
        const isFree = plan.price === 0 || plan.id === 5;
        const buttonClass = 'btn-primary';
        const isSale = plan.sale && plan.sale > 0;
        const finalPrice = isSale ? plan.price : plan.priceBeforeSale;
        const discountPercentage = plan.sale || 0;
        const badgeClass = isFree ? 'bg-success' : isSale ? 'bg-danger' : 'bg-primary';
        
        const disabledBtn = isFree ? !isDisabled : isDisabled;
        const isAlreadySelected = isFree && plan.selected;
        const finalDisabled = disabledBtn || isAlreadySelected;

        return (
            <div className="col-lg-4 col-md-6" key={plan.id}>
                <div className={`card stretch pricing-card ${finalDisabled ? 'opacity-50' : ''}`}>
                    <div className={`card-header text-center bg-soft-primary p-4 ${finalDisabled ? 'bg-gray-200' : ''}`}>
                        <div className={`badge ${badgeClass} text-white mb-2`}>{plan.name}</div>
                        <h5 className="mb-0 fw-bold">{plan.description}</h5>
                        {isSale && (
                            <span className="text-danger fw-bold fs-12 d-block mt-2">
                                {discountPercentage}% تخفیف!
                            </span>
                        )}
                    </div>
                    <div className="card-body text-center py-5">
                        <div className="d-flex align-items-center justify-content-center">
                            <span className="display-4 fw-bolder text-dark me-2">{formatPrice(finalPrice)}</span>
                        </div>
                        {isSale && (
                            <div className="text-muted text-decoration-line-through mt-1">
                                {formatPrice(plan.priceBeforeSale)}
                            </div>
                        )}
                        <div className="fs-14 text-muted mt-3">مدت اعتبار: {plan.duration_days} روز</div>
                        <div className="fs-14 text-muted">حداکثر تعداد اقساط: {plan.max_installments} ماه</div>

                        <ul className="list-unstyled text-start mt-5 text-dark">
                            {subscriptionFeatures.map((feature, index) => (
                                <li className="mb-3 d-flex align-items-center" key={index}>
                                    <i className="feather-check text-success me-2"></i> {feature}
                                </li>
                            ))}
                        </ul>

                    </div>
                    <div className="card-footer text-center">
                        <button 
                            className={`btn w-100 ${buttonClass}`} 
                            disabled={finalDisabled}
                            onClick={() => {
                                if (!finalDisabled) {
                                    // if (isFree) {
                                    //     addSubscribe(plan.id);
                                    // } else {
                                    //     handleOpenBankModal(plan.id);
                                    // }
                                    addSubscribe(plan.id);
                                }
                            }}
                        >
                            {finalDisabled ? (isAlreadySelected ? 'قبلاً انتخاب شده' : 'غیرفعال شده') : 'انتخاب پلن'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-xl-12">
                    <div className="card stretch">
                        <div className="card-header">
                            <h5 className="card-title">انتخاب پلن اشتراک</h5>
                        </div>
                        <div className="card-body">
                            <div className="alert alert-info text-center mb-4">
                                <p className="mb-1 fw-bold">جهت خرید اشتراک لطفا طرح مورد نظر را انتخاب کنید و مبالغ اشتراک را به حساب زیر واریز و رسید آن را بهمراه نام کاربری در شبکه‌های اجتماعی ارسال فرمائید:</p>
                                <div className="row g-2 fs-14 mt-2">
                                    <div className="col-md-4"><strong>نام بانک:</strong> بانک پاسارگاد</div>
                                    <div className="col-md-4"><strong>شماره حساب:</strong> 1001.8100.13869555.1</div>
                                    <div className="col-md-4"><strong>شماره شبا:</strong> IR500570100181013869555101</div>
                                    <div className="col-md-4"><strong>شماره کارت:</strong> 5022291303524399</div>
                                    <div className="col-md-4"><strong>نام صاحب حساب:</strong> شرکت فراز اندیشه آرسین</div>
                                    <div className="col-md-4"><strong>ID تلگرام/واتس‌اپ:</strong> @f_aarsin / 09022204058</div>
                                </div>
                            </div>

                            <div className="row g-4 justify-content-center">
                                {plans.length > 0 ? (
                                    plans.map(renderCard)
                                ) : (
                                    <div className="col-12"><div className="alert alert-info">پلن اشتراکی برای نمایش وجود ندارد.</div></div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {bankModal.show && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header rtl" style={{ direction: 'rtl' }}>
                                <h5 className="modal-title m-0">انتخاب درگاه پرداخت</h5>
                                <button type="button" className="btn-close m-0 ms-auto" style={{ marginRight: 'auto', marginLeft: 0 }} onClick={handleCloseBankModal} disabled={paymentLoading}></button>
                            </div>
                            <div className="modal-body text-center py-5" style={{ direction: 'rtl' }}>
                                {paymentLoading ? (
                                    <div className="d-flex flex-column align-items-center bg-white justify-content-center w-100 h-100">
                                        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span className="fs-5 mt-2">در حال انتقال به درگاه پرداخت...</span>
                                    </div>
                                ) : (
                                    <>
                                        <p className="fs-5 mb-5">درگاه پرداخت خود را انتخاب کنید</p>
                                        <div 
                                            className="card card-body align-items-center justify-content-center p-4 d-inline-flex mx-auto border-1"
                                            style={{ cursor: 'pointer', borderRadius: '10px', borderColor: '#a6a6a6', backgroundColor: 'rgba(241,241,241,0.45)', width: '200px' }}
                                            onClick={goToBank}
                                        >
                                            <div className="fw-bold fs-5 text-dark">درگاه پاسارگاد</div>
                                        </div>
                                    </>
                                )}
                            </div>
                            {!paymentLoading && (
                                <div className="modal-footer" style={{ direction: 'rtl' }}>
                                    <button type="button" className="btn btn-danger px-4 py-2" onClick={handleCloseBankModal}>لغو</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {toast.show && (
                <div className="position-fixed p-3" style={{ top: 20, left: 20, zIndex: 9999 }}>
                    <div className={`toast show bg-${toast.type === 'success' ? 'success' : 'danger'} text-white`} role="alert">
                        <div className="toast-header" style={{ direction: 'rtl' }}>
                            <strong className="me-auto">{toast.type === 'success' ? 'موفق' : 'خطا'}</strong>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setToast({ ...toast, show: false })}></button>
                        </div>
                        <div className="toast-body">
                            {toast.message}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SubscriptionPlans;
